import { z } from 'zod';
import { planoSchema, subscriptionStatusSchema } from './assinatura.js';

/**
 * Backoffice El Shaday (M17).
 *
 * O que a equipe vê aqui é **sobre** as igrejas: plano, uso, saúde da conta.
 * Nunca o conteúdo delas — para isso existe a impersonação, que pede motivo,
 * dura pouco, avisa a igreja e fica registrada.
 */

// ── A lista de igrejas ───────────────────────────────────────────────────────

/*
 * As ordens que o painel oferece são as que se respondem sem calcular o uso de
 * todas as igrejas. Ordenar por "mais pessoas" exigiria contar as escalas de
 * cada igreja do cadastro só para montar a primeira página — e a pergunta que
 * o painel existe para responder ("quem precisa de mim agora?") é melhor
 * respondida pelo último acesso do que pelo tamanho.
 */
export const ORDENS_DA_LISTA = ['recentes', 'nome', 'ultimo_acesso'] as const;
export const ordemDaListaSchema = z.enum(ORDENS_DA_LISTA);
export type OrdemDaLista = z.infer<typeof ordemDaListaSchema>;

export const ORDEM_LABEL: Record<OrdemDaLista, string> = {
  recentes: 'Mais recentes',
  nome: 'Nome',
  ultimo_acesso: 'Último acesso',
};

export const filtroDeIgrejasSchema = z.object({
  busca: z.string().trim().max(120).optional(),
  plano: planoSchema.optional(),
  status: subscriptionStatusSchema.optional(),
  ordem: ordemDaListaSchema.default('recentes'),
  pagina: z.coerce.number().int().min(1).default(1),
});
export type FiltroDeIgrejas = z.infer<typeof filtroDeIgrejasSchema>;

export const igrejaNoBackofficeSchema = z.object({
  id: z.string().uuid(),
  nome: z.string(),
  slug: z.string(),
  denominacao: z.string().nullable(),
  plano: planoSchema,
  planoNome: z.string(),
  status: subscriptionStatusSchema,
  /** Dias que faltam no trial. Nulo fora do trial. */
  diasDeTrial: z.number().int().nullable(),
  criadaEm: z.string().datetime(),
  /** Quando alguém dessa igreja entrou pela última vez. */
  ultimoAcesso: z.string().datetime().nullable(),
  pessoasAtivas: z.number().int(),
  congregacoes: z.number().int(),
  eventosNoMes: z.number().int(),
  armazenamentoGb: z.number(),
  mensagensNoMes: z.number().int(),
  /** A igreja já publicou a primeira escala? É a régua de ativação. */
  ativada: z.boolean(),
});
export type IgrejaNoBackoffice = z.infer<typeof igrejaNoBackofficeSchema>;

export const listaDeIgrejasSchema = z.object({
  items: z.array(igrejaNoBackofficeSchema),
  total: z.number().int(),
  pagina: z.number().int(),
  paginas: z.number().int(),
});
export type ListaDeIgrejas = z.infer<typeof listaDeIgrejasSchema>;

// ── Métricas do negócio ──────────────────────────────────────────────────────

export const coorteSchema = z.object({
  /** O mês em que as igrejas dessa coorte se cadastraram (AAAA-MM). */
  mes: z.string(),
  igrejas: z.number().int(),
  /** Quantas ainda estavam ativas 30, 60 e 90 dias depois. */
  aos30: z.number().int(),
  aos60: z.number().int(),
  aos90: z.number().int(),
});
export type Coorte = z.infer<typeof coorteSchema>;

export const metricasDoNegocioSchema = z.object({
  /** Receita recorrente mensal, em centavos. O anual entra rateado por mês. */
  mrr: z.number().int(),
  igrejasPagantes: z.number().int(),
  igrejasEmAvaliacao: z.number().int(),
  igrejasSomenteLeitura: z.number().int(),
  /** Cancelamentos no mês sobre quem pagava no início dele, de 0 a 100. */
  churn: z.number(),
  /** Quantas avaliações viraram assinatura, de 0 a 100. */
  conversaoDoTrial: z.number(),
  /** Quantas igrejas publicaram a primeira escala, de 0 a 100. */
  ativacao: z.number(),
  novasNoMes: z.number().int(),
  coortes: z.array(coorteSchema),
});
export type MetricasDoNegocio = z.infer<typeof metricasDoNegocioSchema>;

// ── Painel de saúde ──────────────────────────────────────────────────────────

export const SINAIS = ['ok', 'atencao', 'falha'] as const;
export const sinalSchema = z.enum(SINAIS);
export type Sinal = z.infer<typeof sinalSchema>;

export const linhaDeSaudeSchema = z.object({
  chave: z.string(),
  rotulo: z.string(),
  sinal: sinalSchema,
  /** O número que importa: quantos parados, quantos falharam, quantos caídos. */
  valor: z.number().int(),
  detalhe: z.string(),
});
export type LinhaDeSaude = z.infer<typeof linhaDeSaudeSchema>;

export const painelDeSaudeSchema = z.object({
  linhas: z.array(linhaDeSaudeSchema),
  /** O pior sinal entre as linhas: é ele que colore o cabeçalho. */
  sinal: sinalSchema,
  medidoEm: z.string().datetime(),
});
export type PainelDeSaude = z.infer<typeof painelDeSaudeSchema>;

// ── Gestão da assinatura pelo backoffice ─────────────────────────────────────

export const ACOES_DA_ASSINATURA = [
  'estender_trial',
  'mudar_plano',
  'cortesia',
  'suspender',
  'reativar',
] as const;
export const acaoDaAssinaturaSchema = z.enum(ACOES_DA_ASSINATURA);
export type AcaoDaAssinatura = z.infer<typeof acaoDaAssinaturaSchema>;

export const ACAO_LABEL: Record<AcaoDaAssinatura, string> = {
  estender_trial: 'Estender a avaliação',
  mudar_plano: 'Mudar de plano',
  cortesia: 'Marcar como cortesia',
  suspender: 'Suspender a conta',
  reativar: 'Reativar a conta',
};

/** O motivo é obrigatório em toda ação: é ele que a auditoria guarda. */
const motivoSchema = z
  .string()
  .trim()
  .min(10, 'Escreva o motivo: é ele que fica registrado na auditoria')
  .max(500);

export const acaoNaAssinaturaSchema = z.discriminatedUnion('acao', [
  z.object({
    acao: z.literal('estender_trial'),
    dias: z.coerce.number().int().min(1).max(90),
    motivo: motivoSchema,
  }),
  z.object({ acao: z.literal('mudar_plano'), plano: planoSchema, motivo: motivoSchema }),
  z.object({ acao: z.literal('cortesia'), meses: z.coerce.number().int().min(1).max(36), motivo: motivoSchema }),
  z.object({ acao: z.literal('suspender'), motivo: motivoSchema }),
  z.object({ acao: z.literal('reativar'), motivo: motivoSchema }),
]);
export type AcaoNaAssinaturaInput = z.infer<typeof acaoNaAssinaturaSchema>;

// ── Impersonação ─────────────────────────────────────────────────────────────

/** Meia hora é tempo de resolver um chamado, e curto para um acesso desses. */
export const MINUTOS_DE_IMPERSONACAO = 30;

export const abrirImpersonacaoSchema = z.object({
  tenantId: z.string().uuid(),
  motivo: z
    .string()
    .trim()
    .min(10, 'Diga o que você vai fazer lá dentro. A igreja vai ler este texto.')
    .max(300),
  /** O número do chamado, quando houver. Ajuda a ligar o acesso ao pedido. */
  chamado: z.string().trim().max(60).nullish(),
});
export type AbrirImpersonacaoInput = z.infer<typeof abrirImpersonacaoSchema>;

export const impersonacaoSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  igreja: z.string(),
  /** Quem entrou. A igreja vê o nome, e não um "suporte" anônimo. */
  suporte: z.string(),
  motivo: z.string(),
  chamado: z.string().nullable(),
  iniciadaEm: z.string().datetime(),
  expiraEm: z.string().datetime(),
  encerradaEm: z.string().datetime().nullable(),
});
export type Impersonacao = z.infer<typeof impersonacaoSchema>;

/** O que a faixa fixa mostra para quem está dentro da igreja como suporte. */
export const faixaDeImpersonacaoSchema = z.object({
  igreja: z.string(),
  motivo: z.string(),
  expiraEm: z.string().datetime(),
});
export type FaixaDeImpersonacao = z.infer<typeof faixaDeImpersonacaoSchema>;

// ── Feature flags e comunicados ──────────────────────────────────────────────

export const featureFlagSchema = z.object({
  chave: z.string(),
  rotulo: z.string(),
  descricao: z.string(),
  /** Ligada para todo mundo, quando não há regra por igreja. */
  padrao: z.boolean(),
  /** `null` quando a igreja segue o padrão. */
  daIgreja: z.boolean().nullable(),
  ligada: z.boolean(),
});
export type FeatureFlag = z.infer<typeof featureFlagSchema>;

export const definirFlagSchema = z.object({
  chave: z.string().trim().min(2).max(60),
  /** `null` devolve a igreja ao padrão do produto. */
  ligada: z.boolean().nullable(),
  motivo: z.string().trim().min(5).max(300),
});
export type DefinirFlagInput = z.infer<typeof definirFlagSchema>;

/**
 * As chaves que o produto conhece hoje. Uma flag sem uso é dívida: ela só entra
 * aqui quando existe código do outro lado perguntando por ela.
 */
export const FLAGS_DO_PRODUTO = [
  {
    chave: 'motor_de_escala_beta',
    rotulo: 'Auto-escala em teste',
    descricao: 'Libera a sugestão automática de escala para igrejas fora do plano Ministério.',
    padrao: false,
  },
  {
    chave: 'ajuda_em_video',
    rotulo: 'Vídeos na central de ajuda',
    descricao: 'Mostra os vídeos gravados junto dos artigos de ajuda.',
    padrao: false,
  },
] as const;

export const COMUNICADO_TONS = ['informativo', 'atencao', 'manutencao'] as const;
export const comunicadoTomSchema = z.enum(COMUNICADO_TONS);
export type ComunicadoTom = z.infer<typeof comunicadoTomSchema>;

export const COMUNICADO_TOM_LABEL: Record<ComunicadoTom, string> = {
  informativo: 'Novidade',
  atencao: 'Aviso',
  manutencao: 'Manutenção',
};

export const comunicadoGlobalSchema = z.object({
  id: z.string().uuid(),
  tom: comunicadoTomSchema,
  titulo: z.string(),
  mensagem: z.string(),
  /** Quando some sozinho. Nulo é "até alguém desligar". */
  ateQuando: z.string().datetime().nullable(),
  ativo: z.boolean(),
});
export type ComunicadoGlobal = z.infer<typeof comunicadoGlobalSchema>;

export const publicarComunicadoSchema = z.object({
  tom: comunicadoTomSchema.default('informativo'),
  titulo: z.string().trim().min(4, 'Escreva um título curto').max(120),
  mensagem: z.string().trim().min(10, 'Escreva a mensagem').max(600),
  ateQuando: z.string().datetime().nullish(),
});
export type PublicarComunicadoInput = z.infer<typeof publicarComunicadoSchema>;
